import { useState, useEffect } from 'preact/hooks';
import { getCalendarData, getUsers, getDepartments, getLocations, getHolidays } from '../lib/api';

export function CalendarPage({ user, workspace }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState('month'); // 'day', 'week', 'month'
  const [calendarData, setCalendarData] = useState({ leaves: [], holidays: [] });
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, [currentDate, viewType]);

  async function loadData() {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();

      const [calData, usersData, deptsData, locsData] = await Promise.all([
        getCalendarData(startDate, endDate),
        getUsers(),
        getDepartments(),
        getLocations()
      ]);

      setCalendarData(calData);
      setUsers(usersData || []);
      setDepartments(deptsData || []);
      setLocations(locsData || []);
    } catch (error) {
      console.error('Failed to load calendar:', error);
    } finally {
      setLoading(false);
    }
  }

  function getDateRange() {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = currentDate.getDate();

    if (viewType === 'day') {
      const dateStr = currentDate.toISOString().split('T')[0];
      return { startDate: dateStr, endDate: dateStr };
    }

    if (viewType === 'week') {
      const day = currentDate.getDay();
      const diff = date - day + (day === 0 ? -6 : 1);
      const monday = new Date(currentDate);
      monday.setDate(diff);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return {
        startDate: monday.toISOString().split('T')[0],
        endDate: sunday.toISOString().split('T')[0]
      };
    }

    // Month view
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return {
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    };
  }

  function navigate(direction) {
    const newDate = new Date(currentDate);
    if (viewType === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  }

  function goToToday() {
    setCurrentDate(new Date());
  }

  function getLeaveColor(leaveType) {
    const colors = {
      'Vacation': '#8B5CF6',
      'Vacation Leave': '#8B5CF6',
      'Sick': '#EF4444',
      'Sick Leave': '#EF4444',
      'Sick day': '#EF4444',
      'Maternity': '#EC4899',
      'Maternity Leave': '#EC4899'
    };
    return colors[leaveType] || '#6B7280';
  }

  // Filter leaves
  const filteredLeaves = (calendarData.leaves || []).filter(leave => {
    if (departmentFilter !== 'all') {
      const leaveUser = users.find(u => u.id === leave.user_id);
      if (leaveUser?.department_id !== departmentFilter) return false;
    }
    if (locationFilter !== 'all') {
      const leaveUser = users.find(u => u.id === leave.user_id);
      if (leaveUser?.location_id !== locationFilter) return false;
    }
    if (leaveTypeFilter !== 'all' && leave.leave_type !== leaveTypeFilter) return false;
    if (statusFilter !== 'all' && leave.status !== statusFilter) return false;
    return true;
  });

  // Get unique leave types
  const leaveTypes = [...new Set((calendarData.leaves || []).map(l => l.leave_type))];

  const getViewLabel = () => {
    if (viewType === 'day') {
      return currentDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (viewType === 'week') {
      const { startDate, endDate } = getDateRange();
      const start = new Date(startDate);
      const end = new Date(endDate);
      return `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  return (
    <div class="calendar-page">
      {/* Header */}
      <div class="calendar-page-header">
        <h2>Team Calendar</h2>
        <div class="calendar-actions">
          <button
            class={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div class="card filters-panel" style="margin-bottom: 1rem;">
          <div class="card-body">
            <div class="filters-grid">
              <div class="filter-group">
                <label class="filter-label">Department</label>
                <select
                  class="form-select form-select-sm"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="all">All Departments</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div class="filter-group">
                <label class="filter-label">Location</label>
                <select
                  class="form-select form-select-sm"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="all">All Locations</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>

              <div class="filter-group">
                <label class="filter-label">Leave Type</label>
                <select
                  class="form-select form-select-sm"
                  value={leaveTypeFilter}
                  onChange={(e) => setLeaveTypeFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  {leaveTypes.map(lt => (
                    <option key={lt} value={lt}>{lt}</option>
                  ))}
                </select>
              </div>

              <div class="filter-group">
                <label class="filter-label">Status</label>
                <select
                  class="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      <div class="card">
        <div class="card-body">
          {/* Calendar Navigation */}
          <div class="calendar-header">
            <div class="calendar-nav">
              <button class="btn btn-sm btn-secondary" onClick={() => navigate(-1)}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button class="btn btn-sm btn-secondary" onClick={goToToday}>
                Today
              </button>
              <button class="btn btn-sm btn-secondary" onClick={() => navigate(1)}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <h3 class="calendar-title">{getViewLabel()}</h3>

            <div class="view-toggle">
              <button
                class={`toggle-btn ${viewType === 'day' ? 'active' : ''}`}
                onClick={() => setViewType('day')}
              >
                Day
              </button>
              <button
                class={`toggle-btn ${viewType === 'week' ? 'active' : ''}`}
                onClick={() => setViewType('week')}
              >
                Week
              </button>
              <button
                class={`toggle-btn ${viewType === 'month' ? 'active' : ''}`}
                onClick={() => setViewType('month')}
              >
                Month
              </button>
            </div>
          </div>

          {loading ? (
            <div class="loading-screen" style="min-height: 400px;">
              <div class="spinner" />
            </div>
          ) : viewType === 'day' ? (
            <DayView
              date={currentDate}
              leaves={filteredLeaves}
              holidays={calendarData.holidays}
              getLeaveColor={getLeaveColor}
            />
          ) : viewType === 'week' ? (
            <WeekView
              date={currentDate}
              leaves={filteredLeaves}
              holidays={calendarData.holidays}
              getLeaveColor={getLeaveColor}
            />
          ) : (
            <MonthView
              date={currentDate}
              leaves={filteredLeaves}
              holidays={calendarData.holidays}
              getLeaveColor={getLeaveColor}
            />
          )}
        </div>
      </div>

      {/* Legend */}
      <div class="calendar-legend-bar">
        <div class="legend-item">
          <span class="legend-dot vacation" />
          <span>Vacation</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot sick" />
          <span>Sick</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot maternity" />
          <span>Maternity</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot pending-stripe" />
          <span>Pending</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot holiday" />
          <span>Holiday</span>
        </div>
      </div>
    </div>
  );
}

function DayView({ date, leaves, holidays, getLeaveColor }) {
  const dateStr = date.toISOString().split('T')[0];
  const dayLeaves = leaves.filter(l => dateStr >= l.start_date && dateStr <= l.end_date);
  const dayHolidays = holidays.filter(h => h.date === dateStr);

  return (
    <div class="day-view">
      {dayHolidays.length > 0 && (
        <div class="day-holidays">
          {dayHolidays.map(h => (
            <div key={h.id} class="holiday-banner">
              🎉 {h.name}
            </div>
          ))}
        </div>
      )}

      {dayLeaves.length > 0 ? (
        <div class="day-leaves-list">
          {dayLeaves.map(leave => (
            <div
              key={leave.id}
              class={`day-leave-item ${leave.status}`}
              style={{ borderLeftColor: getLeaveColor(leave.leave_type) }}
            >
              <div class="leave-user-info">
                <div class="avatar">
                  {leave.avatar_url ? <img src={leave.avatar_url} alt="" /> : leave.user_name?.charAt(0)}
                </div>
                <div>
                  <div class="user-name">{leave.user_name}</div>
                  <div class="leave-type" style={{ color: getLeaveColor(leave.leave_type) }}>
                    {leave.icon} {leave.leave_type}
                  </div>
                </div>
              </div>
              <div class="leave-dates">
                {leave.start_date === leave.end_date ? (
                  leave.start_date
                ) : (
                  `${leave.start_date} - ${leave.end_date}`
                )}
              </div>
              <div class={`leave-status badge badge-${leave.status}`}>
                {leave.status}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div class="empty-state" style="padding: 3rem;">
          <div class="empty-state-icon">📅</div>
          <h3>No leaves on this day</h3>
        </div>
      )}
    </div>
  );
}

function WeekView({ date, leaves, holidays, getLeaveColor }) {
  const weekDays = getWeekDays(date);
  const today = new Date().toISOString().split('T')[0];

  return (
    <div class="week-view">
      <div class="week-grid">
        {weekDays.map(day => {
          const dateStr = day.toISOString().split('T')[0];
          const dayLeaves = leaves.filter(l => dateStr >= l.start_date && dateStr <= l.end_date);
          const dayHolidays = holidays.filter(h => h.date === dateStr);
          const isToday = dateStr === today;
          const isWeekend = [0, 6].includes(day.getDay());

          return (
            <div key={dateStr} class={`week-day ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}>
              <div class="week-day-header">
                <span class="week-day-name">{day.toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                <span class="week-day-date">{day.getDate()}</span>
              </div>
              <div class="week-day-content">
                {dayHolidays.map(h => (
                  <div key={h.id} class="week-event holiday">
                    🎉 {h.name}
                  </div>
                ))}
                {dayLeaves.slice(0, 4).map(leave => (
                  <div
                    key={leave.id}
                    class={`week-event leave ${leave.status}`}
                    style={{ backgroundColor: getLeaveColor(leave.leave_type) }}
                    title={`${leave.user_name} - ${leave.leave_type}`}
                  >
                    {leave.user_name?.split(' ')[0]}
                  </div>
                ))}
                {dayLeaves.length > 4 && (
                  <div class="week-event more">+{dayLeaves.length - 4} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({ date, leaves, holidays, getLeaveColor }) {
  const calendarDays = generateCalendarDays(date, leaves, holidays, getLeaveColor);

  return (
    <div class="calendar-grid">
      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
        <div key={day} class="calendar-day-header">{day}</div>
      ))}

      {calendarDays.map((day, index) => (
        <div
          key={index}
          class={`calendar-day ${day.isOtherMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''} ${day.isWeekend ? 'weekend' : ''}`}
        >
          <div class="calendar-date">{day.date}</div>
          {day.events.slice(0, 3).map((event, i) => (
            <div
              key={i}
              class={`calendar-event ${event.status}`}
              style={{ backgroundColor: event.color, color: 'white' }}
              title={`${event.userName} - ${event.type}`}
            >
              {event.userName?.split(' ')[0]}
            </div>
          ))}
          {day.events.length > 3 && (
            <div class="calendar-event more">+{day.events.length - 3} more</div>
          )}
          {day.holidays.map((holiday, i) => (
            <div
              key={i}
              class="calendar-event holiday"
              title={holiday.name}
            >
              🎉 {holiday.name.length > 10 ? holiday.name.slice(0, 10) + '...' : holiday.name}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function getWeekDays(date) {
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date);
  monday.setDate(diff);

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
}

function generateCalendarDays(currentDate, leaves, holidays, getLeaveColor) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  let startDayOfWeek = firstDay.getDay();
  startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

  const days = [];
  const today = new Date().toISOString().split('T')[0];

  // Add days from previous month
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const d = prevMonthLastDay - i;
    const prevMonth = month === 0 ? 12 : month;
    const prevYear = month === 0 ? year - 1 : year;
    const dateStr = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayOfWeek = new Date(dateStr).getDay();

    days.push({
      date: d,
      dateStr,
      isOtherMonth: true,
      isToday: false,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      events: [],
      holidays: []
    });
  }

  // Add days of current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayOfWeek = new Date(dateStr).getDay();

    const events = (leaves || [])
      .filter(leave => dateStr >= leave.start_date && dateStr <= leave.end_date)
      .map(leave => ({
        id: leave.id,
        userName: leave.user_name,
        type: leave.leave_type,
        icon: leave.icon || '📅',
        color: getLeaveColor(leave.leave_type),
        status: leave.status
      }));

    const dayHolidays = (holidays || []).filter(h => h.date === dateStr);

    days.push({
      date: d,
      dateStr,
      isOtherMonth: false,
      isToday: dateStr === today,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      events,
      holidays: dayHolidays
    });
  }

  // Add days from next month
  const remainingDays = 42 - days.length;
  for (let d = 1; d <= remainingDays; d++) {
    const nextMonth = month === 11 ? 1 : month + 2;
    const nextYear = month === 11 ? year + 1 : year;
    const dateStr = `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayOfWeek = new Date(dateStr).getDay();

    days.push({
      date: d,
      dateStr,
      isOtherMonth: true,
      isToday: false,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      events: [],
      holidays: []
    });
  }

  return days;
}
